import type { CharacterIPMedia, CharacterIPPanelAction, CharacterIPValue } from '$/components/CharacterIPPanel/types';
import type { MyIpCreationHandoff, MyIpCreationTarget } from '$/domains/myIp/creationHandoff';
import type { MyIpItem, MyIpProject } from '$/domains/myIp/types';
import { mtstatTrack } from '$/utils/mtstat';

export type MyIpPanelLocation = 'ip_page' | 'ip_pop';
export type MyIpPanelMode = 'view' | 'edit';
export type MyIpCreateEntranceLocation = 'home_page_have_ip' | 'home_page_no_ip' | 'agent_chat' | 'canvas_top_bar';
export type MyIpPanelClickType = CharacterIPPanelAction | 'look_ip_role';
export type MyIpPageType = 'first_visit' | 'create_state' | 'edit_state';
export type MyIpApplySource = 'basic_settings' | 'more_assets' | 'asset_personal_ip_apply' | 'asset_official_ip_apply' | 'my_ip_detail_page' | 'my_ip_page' | 'case_detal_page_click_ip';

export interface MyIpTrackingContext {
  roomId: string;
  canvasId?: string;
  projectId?: string;
  ipId: string;
}

/** 把未定义的上下文字段归一为空字符串，保证 Dayu 普通参数始终使用字符串。 */
function text(value: unknown): string {
  return value == null ? '' : String(value);
}

/** 创建成功取得真实 IP 后上报入口，首页与画布均复用同一参数格式。 */
export function trackMyIpCreateEntranceClick(
  context: Pick<MyIpTrackingContext, 'roomId' | 'projectId' | 'ipId'>,
  location: MyIpCreateEntranceLocation,
): void {
  mtstatTrack('roboneo_ip_create_entrance_click', {
    room_id: text(context.roomId),
    project_id: text(context.projectId),
    ip_id: text(context.ipId),
    location,
  });
}

/** 只收集可用 HTTP(S) 地址，避免把 asset id、blob 地址或普通描述误当成埋点媒体。 */
function mediaUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? value : '';
  } catch {
    return '';
  }
}

/** 递归提取摘要内真实媒体 URL，并保持首次出现顺序。 */
function collectSummaryMediaUrls(value: unknown, found = new Set<string>()): string[] {
  const url = mediaUrl(value);
  if (url) found.add(url);
  else if (Array.isArray(value)) value.forEach((entry) => collectSummaryMediaUrls(entry, found));
  else if (value && typeof value === 'object') Object.values(value).forEach((entry) => collectSummaryMediaUrls(entry, found));
  return [...found];
}

/** 空字符串不能挡住后续来源，否则当前项目缺字段时画布身份会一直上报为空。 */
function firstId(...values: Array<string | undefined>): string | undefined {
  return values.find((value) => typeof value === 'string' && value.trim())?.trim();
}

/** 创建页优先使用已打开项目；缺字段时再从交接目标、原画布、进度和页面参数回填。 */
export function getMyIpAgentTrackingContext({
  roomId, ipId, currentProject, handoff, canvas,
}: {
  roomId: string;
  ipId: string;
  currentProject?: MyIpCreationTarget;
  handoff?: MyIpCreationHandoff;
  /** 独立详情进入编辑页时只有返回地址上的画布，不参与创建交接。 */
  canvas?: { projectId?: string; canvasId?: string; };
}): MyIpTrackingContext {
  const canvasOrigin = handoff?.origin.kind === 'canvas' ? handoff.origin : undefined;
  return {
    roomId,
    ipId,
    projectId: firstId(
      currentProject?.projectId,
      handoff?.target?.projectId,
      canvasOrigin?.projectId,
      handoff?.progress?.projectId,
      canvas?.projectId,
    ),
    canvasId: firstId(
      currentProject?.canvasId,
      handoff?.target?.canvasId,
      canvasOrigin?.canvasId,
      handoff?.progress?.canvasId,
      canvas?.canvasId,
    ),
  };
}

const VIEW_ONLY_PANEL_CLICKS: ReadonlySet<MyIpPanelClickType> = new Set(['edit', 'look_ip_role']);
const EDIT_ONLY_PANEL_CLICKS: ReadonlySet<MyIpPanelClickType> = new Set([
  'save_role', 'main_image_recreate', 'three_view_recreate', 'ip_name', 'ip_feature',
]);

/** 查看专属动作固定走 view_click，编辑专属动作固定走 edit_click，其余跟随面板当前态。 */
function resolveMyIpPanelMode(clickType: MyIpPanelClickType, mode: MyIpPanelMode): MyIpPanelMode {
  if (VIEW_ONLY_PANEL_CLICKS.has(clickType)) return 'view';
  if (EDIT_ONLY_PANEL_CLICKS.has(clickType)) return 'edit';
  return mode;
}

/** 上报 IP 详情页或详情弹窗按钮点击；查看态与编辑态事件按 Dayu 8206 拆分，loaction 保留原始字段名。 */
export function trackMyIpPanelClick(
  context: MyIpTrackingContext,
  clickType: MyIpPanelClickType,
  location: MyIpPanelLocation,
  mode: MyIpPanelMode,
): void {
  const eventId = resolveMyIpPanelMode(clickType, mode) === 'edit'
    ? 'roboneo_ippage_edit_click'
    : 'roboneo_ippage_view_click';
  mtstatTrack(eventId, {
    room_id: text(context.roomId),
    canvas_id: text(context.canvasId),
    project_id: text(context.projectId),
    ip_id: text(context.ipId),
    click_type: clickType,
    loaction: location,
  });
}

/** 独立详情页没有画布身份时只带 IP 会话；click_type 由查看态入口传入。 */
export function trackMyIpDetailViewClick(
  item: Pick<MyIpItem, 'ipId' | 'sourceSessionId'>,
  clickType: MyIpPanelClickType,
): void {
  trackMyIpPanelClick({
    roomId: item.sourceSessionId,
    ipId: item.ipId,
  }, clickType, 'ip_page', 'view');
}

/** 上报创建、生成和编辑三种角色 IP 工作台曝光。 */
export function trackMyIpAgentPageShow(context: MyIpTrackingContext, pageType: MyIpPageType): void {
  mtstatTrack('roboneo_ip_agent_page_show', {
    room_id: text(context.roomId),
    canvas_id: text(context.canvasId),
    project_id: text(context.projectId),
    ip_id: text(context.ipId),
    page_type: pageType,
  });
}

/** 从上传或预设菜单选中音色时上报实际试听地址与音色身份。 */
export function trackMyIpTimbreClick(
  context: MyIpTrackingContext,
  media: CharacterIPMedia,
  source: 'upload' | 'menu',
  location: MyIpPanelLocation,
): void {
  const preset = media.voicePreset;
  mtstatTrack('roboneo_ippage_timbre_click', {
    room_id: text(context.roomId),
    canvas_id: text(context.canvasId),
    project_id: text(context.projectId),
    media_url: mediaUrl(media.url),
    ip_id: text(context.ipId),
    timbre_id: text(media.voiceId ?? preset?.voice_id ?? media.timbre?.voice_id ?? media.timbre?.id),
    source,
    loaction: location,
  });
}

/** 基础设定返回主图、三视图与音色，更多素材仅返回本次勾选的内容地址。 */
export function getMyIpAppliedMediaUrls(
  value: CharacterIPValue,
  source: 'basic_settings' | 'more_assets',
  selectedMaterialIds: readonly string[] = [],
): string[] {
  const candidates = source === 'basic_settings'
    ? [value.main_visual, value.turnaround, value.voice]
    : (value.materials ?? []).filter((media) => selectedMaterialIds.includes(media.id));
  return Array.from(new Set(candidates.map((media) => mediaUrl(media?.url)).filter(Boolean)));
}

/** 上报应用到画布点击；projece_id 沿用需求 8163 的原始字段名。 */
export function trackMyIpApplyCanvasClick(
  context: MyIpTrackingContext,
  mediaUrls: readonly string[],
  sourceFrom: MyIpApplySource,
): void {
  mtstatTrack('roboneo_ip_apply_canvas_click', {
    room_id: text(context.roomId),
    canvas_id: text(context.canvasId),
    projece_id: text(context.projectId),
    media_url: mediaUrls.map(mediaUrl).filter(Boolean).join(','),
    ip_id: text(context.ipId),
    source_from: sourceFrom,
  });
}

/** 每次合法的角色摘要下发均上报一次，不与主档写入的内容去重逻辑合并。 */
export function trackMyIpSummaryCreate({
  roomId, ipId, rawData, message,
}: {
  roomId: string;
  ipId: string;
  rawData: Record<string, unknown>;
  message?: unknown;
}): void {
  const messageRecord = message && typeof message === 'object' ? message as Record<string, unknown> : {};
  const extra = messageRecord.extra && typeof messageRecord.extra === 'object' ? messageRecord.extra as Record<string, unknown> : {};
  let declaredUrls: unknown[] = [];
  if (Array.isArray(rawData.media_urls)) declaredUrls = rawData.media_urls;
  else if (typeof rawData.media_urls === 'string') declaredUrls = rawData.media_urls.split(',');
  const mediaUrls = declaredUrls.length
    ? declaredUrls.map(mediaUrl).filter(Boolean)
    : collectSummaryMediaUrls(rawData.ip_character);
  mtstatTrack('roboneo_ip_summary_create', {
    room_id: text(rawData.room_id ?? roomId),
    ip_id: text(ipId),
    task_id: text(rawData.task_id ?? rawData.sub_task_id ?? messageRecord.task_id ?? extra.task_id ?? extra.sub_task_id),
    agent_id: text(rawData.agent_id ?? rawData.main_agent_id ?? rawData.sub_agent_id
      ?? messageRecord.agent_id ?? extra.main_agent_id ?? extra.sub_agent_id),
    media_urls: mediaUrls.join(','),
  });
}

/** 收集 IP 主档当前可展示媒体，供 IP 节点组创建事件透传内容 URL。 */
export function getMyIpItemMediaUrls(item: MyIpItem): string[] {
  return Array.from(new Set([
    mediaUrl(item.avatarUrl),
    mediaUrl(item.coverUrl),
    mediaUrl(item.coverAssetId),
    ...item.resources.map((resource) => mediaUrl(resource.url)),
  ].filter(Boolean)));
}

export type MyIpCreateLocation = 'ip_card_pop' | 'ip_info_page' | 'ip_relation_card';
export type MyIpSaveEvent = 'canvas_save_click' | 'canvas_save_success' | 'canvas_save_fail';

/** Dayu 仅接受 official/personal 两种 IP 类型，主档接口已在进入页面前完成校验。 */
function getMyIpTrackParams(item: MyIpItem) {
  return {
    ip_id: item.ipId,
    ip_name: item.name,
    ip_type: item.assetType === 'official' ? 'official' : 'personal',
  } as const;
}

/** 仅由成功首屏的访问门禁调用；请求失败和页面重渲染不调用。 */
export function trackMyIpPageView(): void {
  mtstatTrack('my_ip_page_view', {});
}

/** 详情成功展示事件独立于我的 IP 列表；最新 PRD 未指定参数，不推测 creator_id。 */
export function trackMyIpDetailPageView(): void {
  mtstatTrack('ip_detail_page_view', {});
}

/** 仅详情页“为TA创作”的有效点击上报；保留既有埋点兼容。 */
export function trackMyIpDetailCreateClick(): void {
  mtstatTrack('ip_create_click', {});
}

/** 来源由接入页面提供稳定路径，不携带账号、查询参数或部署环境前缀。 */
export function trackMyIpCreateClick(sourcePage: string): void {
  mtstatTrack('my_ip_create_click', { source_page: sourcePage });
}

/** 列表“为TA创作”保留既有事件，Dayu 新事件由 trackMyIpCreateWithIpClick 补报。 */
export function trackMyIpListCreateClick(sourcePage: string): void {
  mtstatTrack('my_ip_create_it', { source_page: sourcePage });
}

/** 卡位从真实 IP 列表按 0 开始，从左到右从上到下递增；分类 UI 已取消，参数按计划传空。 */
export function trackMyIpCardClick(item: MyIpItem, position: number): void {
  mtstatTrack('my_ip_card_click', {
    ip_id: item.ipId,
    ip_name: item.name,
    position,
    category_name: '',
    current_filter_category: '',
  });
}

/** 我的 IP 列表半窗卡片渲染后曝光一次，位置按页面从左到右、从上到下从 0 开始。 */
export function trackMyIpCardPopShow(item: MyIpItem, position: number): void {
  mtstatTrack('roboneo_my_ip_card_pop_show', { ...getMyIpTrackParams(item), position });
}

/** 我的 IP 列表卡片主体点击；“为 Ta 创作”按钮由独立事件统计。 */
export function trackMyIpCardPopClick(item: MyIpItem, position: number): void {
  mtstatTrack('roboneo_my_ip_card_pop_click', { ...getMyIpTrackParams(item), position });
}

/** 统一统计列表卡片、详情页和关联项目卡片中的“为 Ta 创作”点击；position 与卡片曝光口径一致，从 0 开始。 */
export function trackMyIpCreateWithIpClick(
  item: MyIpItem,
  location: MyIpCreateLocation,
  position: number,
): void {
  mtstatTrack('roboneo_my_ip_create_click', { ...getMyIpTrackParams(item), location, position });
}

/** 详情页关联项目卡片出现后曝光一次，位置按关联项目列表从 0 开始。 */
export function trackMyIpRelationCardShow(item: MyIpItem, position: number, project?: MyIpProject): void {
  mtstatTrack('roboneo_my_ip_info_relation_card_show', {
    ...getMyIpTrackParams(item),
    position,
    project_id: text(project?.projectId),
    project_name: text(project?.name),
    media_url: mediaUrl(project?.coverUrl),
  });
}

/** 详情页关联项目卡片点击，位置与曝光事件保持一致。 */
export function trackMyIpRelationCardClick(item: MyIpItem, position: number, project?: MyIpProject): void {
  mtstatTrack('roboneo_my_ip_info_relation_card_click', {
    ...getMyIpTrackParams(item),
    position,
    project_id: text(project?.projectId),
    project_name: text(project?.name),
    media_url: mediaUrl(project?.coverUrl),
  });
}

/** 详情页生成 IP 角色卡点击；当前角色卡入口暂停时不会触发按钮回调。 */
export function trackMyIpRoleCardClick(item: MyIpItem, position: number | string = '', cost: number | string = ''): void {
  mtstatTrack('roboneo_my_ip_info_create_card_click', {
    ...getMyIpTrackParams(item), position, cost,
  });
}

/** 角色卡下载沿用 canvas_save_* 事件，并补齐 IP 角色卡约定的下载字段。 */
export function trackMyIpRoleCardSave(eventId: MyIpSaveEvent, ipId: string): void {
  mtstatTrack(eventId, {
    location: 'ip_role_card',
    video_duration: '',
    save_type: 'image',
    material_id: '',
    ip_id: ipId,
  });
}

/** 更多素材卡片曝光使用当前授权媒体地址，按 IP 和卡片位置去重由展示组件负责。 */
export function trackMyIpMaterialShow(item: MyIpItem, position: number, mediaUrlValue: string): void {
  mtstatTrack('roboneo_ip_info_material_show', {
    ip_id: text(item.ipId),
    ip_name: text(item.name),
    ip_type: item.assetType === 'official' ? 'official' : 'personal',
    position,
    media_url: mediaUrl(mediaUrlValue),
  });
}

/** 更多素材卡片点击使用当前授权媒体地址，保持与曝光事件相同的 IP 参数口径。 */
export function trackMyIpMaterialClick(item: MyIpItem, position: number, mediaUrlValue: string): void {
  mtstatTrack('roboneo_ip_info_material_click', {
    ip_id: text(item.ipId),
    ip_name: text(item.name),
    ip_type: item.assetType === 'official' ? 'official' : 'personal',
    position,
    media_url: mediaUrl(mediaUrlValue),
  });
}
